import { AlertCircle, Calendar, Clock, MapPin, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useGetParentCatalogQuery } from '../../../state/domainRouterApi';
import { mapParentCatalogResponse } from '../camps/viewModels';
import { ModuleSectionNav, type ModuleSectionNavItem } from '../../../components/navigation/ModuleSectionNav';
import { SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(cents: number | null | undefined): string {
  if (cents == null) return '$0';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function statusLabel(value: string | undefined): string {
  switch (value) {
    case 'open':
      return 'Open';
    case 'few-seats':
      return 'Few seats left';
    case 'waitlist':
      return 'Waitlist only';
    case 'closed':
      return 'Closed';
    case 'registered':
      return 'Registered';
    default:
      return value ?? '--';
  }
}

function statusDotColor(value: string | undefined): string {
  switch (value) {
    case 'open':
      return 'var(--aquamarine)';
    case 'few-seats':
      return 'var(--coral)';
    case 'waitlist':
      return 'var(--coral)';
    case 'closed':
      return 'var(--iron-grey)';
    case 'registered':
      return 'var(--jungle)';
    default:
      return 'var(--iron-grey)';
  }
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 w-48 animate-pulse rounded-lg bg-gray-100" />
      <div className="h-20 animate-pulse rounded-xl bg-gray-100" />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-xl bg-gray-100" />
        ))}
      </div>
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-xl bg-gray-100" />
        ))}
      </div>
    </div>
  );
}

function ErrorBanner({ title, detail }: { title: string; detail: string }) {
  return (
    <SupportErrorMessage>
      <p className="font-semibold text-irongrey">{title}</p>
      <p className="mt-1 text-sm text-irongrey/65">{detail}</p>
    </SupportErrorMessage>
  );
}

function KpiCard({
  label,
  value,
  dotColor,
}: {
  label: string;
  value: string;
  dotColor: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: dotColor }} />
        <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">{label}</p>
      </div>
      <p className="text-[32px] font-semibold text-iron-grey">{value}</p>
    </div>
  );
}

function TabNav({ active }: { active: 'catalog' | 'waitlist' | 'registrations' }) {
  const items: ModuleSectionNavItem[] = [
    { id: 'catalog', label: 'Catalog', to: '/parent/portal/camps', end: true },
    { id: 'waitlist', label: 'Waitlist', to: '/parent/portal/camps/waitlist' },
    { id: 'registrations', label: 'Registrations', to: '/parent/portal/camps/registrations' },
  ];

  return (
    <ModuleSectionNav
      items={items}
      activeId={active}
      ariaLabel="Camps navigation"
      testIds={{
        root: 'camps-tab-nav',
        itemPrefix: 'camps-tab-nav-item',
        mobileTrigger: 'camps-tab-nav-mobile-trigger',
        mobileDialog: 'camps-tab-nav-mobile-dialog',
        mobileItemPrefix: 'camps-tab-nav-mobile-item',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Camp Program Card
// ---------------------------------------------------------------------------

function CampProgramCard({
  program,
}: {
  program: ReturnType<typeof mapParentCatalogResponse>['items'][number];
}) {
  return (
    <div className="rounded-xl border border-border bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-2">
          <h3 className="text-lg font-semibold text-iron-grey">{program.name ?? 'Untitled Program'}</h3>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-text-secondary">
            {program.campType ? (
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                {program.campType}
              </span>
            ) : null}
            {program.gradeRange ? (
              <span className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" aria-hidden="true" />
                {program.gradeRange}
              </span>
            ) : null}
            {program.location ? (
              <span className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                {program.location}
              </span>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-text-secondary">
            {program.sessionsCount != null ? (
              <span className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                {program.sessionsCount} session{program.sessionsCount !== 1 ? 's' : ''}
              </span>
            ) : null}
            {program.spotsRemaining != null ? (
              <span>
                {program.spotsRemaining} spot{program.spotsRemaining !== 1 ? 's' : ''} remaining
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col items-end gap-3 sm:min-w-[140px]">
          {program.priceCents != null ? (
            <p className="text-lg font-semibold text-iron-grey">{formatCurrency(program.priceCents)}</p>
          ) : null}

          <div className="flex items-center gap-1.5">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: statusDotColor(program.registrationStatus) }}
            />
            <span className="text-sm font-medium text-iron-grey">
              {statusLabel(program.registrationStatus)}
            </span>
          </div>

          {program.registrationStatus !== 'closed' ? (
            <NavLink
              to={`/parent/portal/camps/${program.programId ?? ''}/register`}
              className="inline-flex items-center rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:opacity-90"
            >
              Register
            </NavLink>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

export function ParentCampsCatalogPage() {
  const { data: rawData, isLoading, isError, error } = useGetParentCatalogQuery();
  const data = mapParentCatalogResponse(rawData);
  const summary = data.registrationSummary;
  const programs = data.items;
  const eligibleCount = data.eligibleStudents.length;

  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState<'all' | 'available' | 'waitlist'>('all');

  const filterOptions = useMemo(() => {
    const types = new Set<string>();
    const locations = new Set<string>();
    for (const p of programs) {
      if (p.campType) types.add(p.campType);
      if (p.location) locations.add(p.location);
    }
    return {
      types: [...types].sort(),
      locations: [...locations].sort(),
    };
  }, [programs]);

  const filteredPrograms = useMemo(() => {
    const needle = searchText.toLowerCase().trim();
    return programs.filter((p) => {
      if (needle && !(p.name ?? '').toLowerCase().includes(needle)) return false;
      if (typeFilter && p.campType !== typeFilter) return false;
      if (locationFilter && p.location !== locationFilter) return false;
      if (availabilityFilter === 'available' && (p.spotsRemaining == null || p.spotsRemaining <= 0)) return false;
      if (availabilityFilter === 'waitlist' && p.registrationStatus !== 'waitlist') return false;
      return true;
    });
  }, [programs, searchText, typeFilter, locationFilter, availabilityFilter]);

  if (isLoading) {
    return (
      <main
        data-testid="parent-camps-catalog"
        data-route-ready="true"
        data-extracted-from="source-faithful:parent-camps-v1.0"
        className="bg-background-light"
      >
        <div className="w-full space-y-6">
          <LoadingSkeleton />
        </div>
      </main>
    );
  }

  if (isError) {
    return (
      <main
        data-testid="parent-camps-catalog"
        data-route-ready="true"
        data-extracted-from="source-faithful:parent-camps-v1.0"
        className="bg-background-light"
      >
        <div className="w-full space-y-6">
          <ErrorBanner
            title="Unable to load camp catalog"
            detail={
              (error && typeof error === 'object' && 'message' in error
                ? String((error as { message?: unknown }).message)
                : null) ??
              'The camp catalog is not available right now. Please try again later.'
            }
          />
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="parent-camps-catalog"
      data-route-ready="true"
      data-extracted-from="source-faithful:parent-camps-v1.0"
      className="bg-background-light"
    >
      <div className="w-full space-y-6">
        {/* Page header */}
        <div data-testid="camps-catalog-header">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--primary)]">
            Parent portal
          </p>
          <h1 className="text-[24px] font-semibold text-iron-grey">Camp Programs</h1>
          {data.seasonLabel ? (
            <p className="mt-1 text-sm text-text-secondary">{data.seasonLabel}</p>
          ) : null}
        </div>

        {/* Family summary banner */}
        <div
          data-testid="camps-family-summary"
          className="rounded-xl border border-border bg-white p-5 shadow-sm"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-iron-grey">{data.familyName ?? 'Your Family'}</p>
              <p className="text-sm text-text-secondary">
                {eligibleCount} eligible camper{eligibleCount !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-[var(--primary)]" aria-hidden="true" />
              <span className="text-sm font-medium text-iron-grey">
                {data.eligibleStudents?.map((s) => s.name).join(', ') ?? '--'}
              </span>
            </div>
          </div>
        </div>

        {/* KPI row */}
        <div data-testid="camps-kpi-row" className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KpiCard
            label="Registered campers"
            value={String(summary.registeredCampers ?? 0)}
            dotColor="var(--aquamarine)"
          />
          <KpiCard
            label="Waitlisted"
            value={String(summary.waitlistedEntries ?? 0)}
            dotColor="var(--coral)"
          />
          <KpiCard
            label="Open windows"
            value={String(summary.openCampWindows ?? 0)}
            dotColor="var(--primary)"
          />
          <KpiCard
            label="Amount due"
            value={formatCurrency(summary.amountDueCents)}
            dotColor="var(--strawberry)"
          />
        </div>

        {/* Tab navigation */}
        <TabNav active="catalog" />

        {/* Gap 1: Filter bar */}
        <div
          data-testid="camps-catalog-filters"
          className="flex flex-wrap items-center gap-3"
        >
          <input
            type="text"
            placeholder="Search camps..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm text-iron-grey placeholder:text-text-secondary focus:border-[var(--primary)] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
          />
          {filterOptions.types.length > 1 ? (
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded-lg border border-border bg-white px-3 py-2 text-sm text-iron-grey focus:border-[var(--primary)] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
            >
              <option value="">All types</option>
              {filterOptions.types.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          ) : null}
          {filterOptions.locations.length > 1 ? (
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="rounded-lg border border-border bg-white px-3 py-2 text-sm text-iron-grey focus:border-[var(--primary)] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
            >
              <option value="">All locations</option>
              {filterOptions.locations.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          ) : null}
          <select
            value={availabilityFilter}
            onChange={(e) => setAvailabilityFilter(e.target.value as 'all' | 'available' | 'waitlist')}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm text-iron-grey focus:border-[var(--primary)] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
          >
            <option value="all">All availability</option>
            <option value="available">Open spots</option>
            <option value="waitlist">Waitlist only</option>
          </select>
          <span className="text-sm text-text-secondary">
            Showing {filteredPrograms.length} of {programs.length} camps
          </span>
        </div>

        {/* Operational deadline banner */}
        {data.deadlineAlert ? (
          <div
            data-testid="camps-deadline-banner"
            className="rounded-xl border border-[var(--coral)] bg-white p-4 shadow-sm"
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-[var(--coral)]" aria-hidden="true" />
              <p className="text-sm font-medium text-iron-grey">{data.deadlineAlert}</p>
            </div>
          </div>
        ) : null}

        {/* Camp program list */}
        <div data-testid="camps-program-list" className="space-y-4">
          {programs.length === 0 ? (
            <div className="rounded-xl border border-border bg-white py-16 text-center shadow-sm">
              <Calendar className="mx-auto h-10 w-10 text-text-secondary" aria-hidden="true" />
              <p className="mt-4 font-semibold text-iron-grey">No camp programs available</p>
              <p className="mt-1 text-sm text-text-secondary">
                Check back later for upcoming camp programs and registration windows.
              </p>
            </div>
          ) : filteredPrograms.length === 0 ? (
            <div className="rounded-xl border border-border bg-white py-12 text-center shadow-sm">
              <p className="font-semibold text-iron-grey">No camps match your filters</p>
              <p className="mt-1 text-sm text-text-secondary">
                Try adjusting your search or filter criteria.
              </p>
            </div>
          ) : (
            filteredPrograms.map((program) => (
              <CampProgramCard key={program.programId ?? program.name} program={program} />
            ))
          )}
        </div>
      </div>
    </main>
  );
}
